/**
 * Local-first mutations for the module SPINE (F2-A).
 *
 * Write to PowerSync → write-path (`db/sync.rs`) with author/editor authz
 * and the course's version stamp. The COMPOSITE ones (`addLessonModuleMutation`,
 * `addResourceModuleMutation`) create the detail entity AND its
 * `course_module` row — the refactor's contract: creating a lesson ALWAYS
 * creates its module (kind='lesson', ref_id=lesson.id).
 */

import {
  course_module,
  course_settings,
  eq,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../../powersync/verified-write";

// ─── course_module ─────────────────────────────────────────────────────────────

export interface AddCourseModuleInput {
  courseId: string;
  unitId: string;
  kind: string;
  refId?: string | null;
  position?: number;
  visibility?: "visible" | "hidden";
  config?: unknown;
  completionRule?: unknown;
}

export async function addCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  input: AddCourseModuleInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(course_module).values({
    id,
    unit_id: input.unitId,
    course_id: input.courseId,
    kind: input.kind,
    ref_id: input.refId ?? null,
    position: input.position ?? 0,
    visibility: input.visibility ?? "visible",
    completion_rule: JSON.stringify(input.completionRule ?? {}),
    config: JSON.stringify(input.config ?? {}),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
  updates: Partial<{
    position: number;
    visibility: "visible" | "hidden";
    config: unknown;
    completion_rule: unknown;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.position !== undefined) {
    patch.position = updates.position;
  }
  if (updates.visibility !== undefined) {
    patch.visibility = updates.visibility;
  }
  if (updates.config !== undefined) {
    patch.config = JSON.stringify(updates.config);
  }
  if (updates.completion_rule !== undefined) {
    patch.completion_rule = JSON.stringify(updates.completion_rule);
  }
  await patchRowVerified(db, course_module, course_module.id, moduleId, patch, options);
}

export async function deleteCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    course_module,
    course_module.id,
    moduleId,
    { deleted_at: nowISO() },
    options,
  );
}

// ─── composites: detail + module ──────────────────────────────────────────────

/**
 * Creates a LESSON and its spine module (kind='lesson', ref_id=lesson.id).
 * Replaces `addLessonMutation` in authoring: the module is the row the
 * player traverses; the lesson remains the detail (blocks/XP).
 */
export async function addLessonModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    position?: number;
    xpReward?: number;
  },
): Promise<{ lessonId: string; moduleId: string }> {
  const lessonId = generateId();
  const now = nowISO();
  await db.insert(lesson).values({
    id: lessonId,
    unit_id: params.unitId,
    course_id: params.courseId,
    title: params.title?.trim() || "New lesson",
    position: params.position ?? 0,
    xp_reward: params.xpReward ?? 10,
    created_at: now,
    updated_at: now,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "lesson",
    refId: lessonId,
    position: params.position ?? 0,
  });
  return { lessonId, moduleId };
}

/**
 * Creates a RESOURCE and its spine module (kind='resource', ref_id=resource.id).
 */
export async function addResourceModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    kind?: "file" | "link" | "text";
    url?: string;
    body?: string;
    position?: number;
  },
): Promise<{ resourceId: string; moduleId: string }> {
  const resourceId = generateId();
  const now = nowISO();
  await db.insert(resource).values({
    id: resourceId,
    course_id: params.courseId,
    kind: params.kind ?? "link",
    title: params.title?.trim() || "New resource",
    url: params.url ?? null,
    body: params.body ?? null,
    created_at: now,
    updated_at: now,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "resource",
    refId: resourceId,
    position: params.position ?? 0,
  });
  return { resourceId, moduleId };
}

// ─── resource (detail) ────────────────────────────────────────────────────────

/**
 * ⚠️ No `scope` on purpose, even though `resource` DOES have `organization_id`
 * (2026-08-13 audit). `addResourceModuleMutation` (above) creates the
 * resource from AUTHORING (`course-editor.tsx`,
 * `addResourceModuleMutation(d, { courseId, unitId, position })`) without
 * passing `organizationId` — a course is authored independent of
 * organization, so `resource.organization_id` stays NULL on that path
 * (confirmed by reading the `insert(resource)`, not just hypothetical). With
 * `scope` by the ACTIVE org, `eq(resource.organization_id, activeOrgId())`
 * would NEVER match against that NULL column and EVERY resource edit/delete
 * would throw `LocalWriteLostError`. Closing it for real requires first
 * populating `organization_id` in the INSERT — out of scope for this change.
 * Signal that would reopen it: `resource`'s INSERT starts receiving a real `organizationId`.
 */
export async function updateResourceMutation(
  db: PowerSyncDrizzleDB,
  resourceId: string,
  updates: Partial<{
    kind: "file" | "link" | "text";
    title: string;
    url: string | null;
    body: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, resource, resource.id, resourceId, updates, options);
}

/** ⚠️ No `scope` on purpose — see the note on `updateResourceMutation`. */
export async function deleteResourceMutation(
  db: PowerSyncDrizzleDB,
  resourceId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, resource, resource.id, resourceId, { deleted_at: nowISO() }, options);
}

// ─── course_settings ───────────────────────────────────────────────────────────

/**
 * Upsert of the per-course settings (id = course_id). PowerSync doesn't
 * support UPSERT over views → manual UPDATE-or-INSERT (same pattern as lesson_progress).
 *
 * ⚠️ The SELECT that decides existence lives inside the window, but the id
 * is DETERMINISTIC (`id = courseId`): a false "doesn't exist" self-heals
 * (INSERT with the same id, doesn't duplicate a different row) instead of
 * duplicating, unlike `submitAssignmentMutation`. Not shielded with
 * `waitForRowPresent` because that same deterministic id belongs to a
 * genuinely just-created course, and waiting out the full backoff there
 * would penalize the normal path (create course → open settings for the first time).
 */
export async function upsertCourseSettingsMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  updates: Partial<{
    grading_scheme: string | null;
    grading_model_id: string | null;
    enabled_module_kinds: string[] | null;
    meeting_provider: string | null;
    completion_config: unknown;
    forum_policy: unknown;
    features: unknown;
    media_settings: unknown;
  }>,
): Promise<void> {
  const now = nowISO();
  const patch: Record<string, unknown> = { updated_at: now };
  if (updates.grading_scheme !== undefined) {
    patch.grading_scheme = updates.grading_scheme;
  }
  if (updates.grading_model_id !== undefined) {
    patch.grading_model_id = updates.grading_model_id;
  }
  if (updates.enabled_module_kinds !== undefined) {
    patch.enabled_module_kinds =
      updates.enabled_module_kinds === null ? null : JSON.stringify(updates.enabled_module_kinds);
  }
  if (updates.meeting_provider !== undefined) {
    patch.meeting_provider = updates.meeting_provider;
  }
  if (updates.completion_config !== undefined) {
    patch.completion_config = JSON.stringify(updates.completion_config ?? {});
  }
  if (updates.forum_policy !== undefined) {
    patch.forum_policy = JSON.stringify(updates.forum_policy ?? {});
  }
  if (updates.features !== undefined) {
    patch.features = JSON.stringify(updates.features ?? {});
  }
  if (updates.media_settings !== undefined) {
    patch.media_settings = JSON.stringify(updates.media_settings ?? {});
  }

  const existing = await db
    .select({ id: course_settings.id })
    .from(course_settings)
    .where(eq(course_settings.id, courseId))
    .limit(1);
  if (existing[0]) {
    await db.update(course_settings).set(patch).where(eq(course_settings.id, courseId));
  } else {
    await db.insert(course_settings).values({ id: courseId, created_at: now, ...patch });
  }
}
